/**
 * useCrossWindowSync (Seth 2026-06-24, two-monitor dispatcher model) — listen for
 * data-change broadcasts from OTHER app windows and invalidate the named react-query
 * keys locally. react-query caches are per-window, so without this a clear/schedule
 * in the 360 window leaves the ambient board window stale until reload. Mounted once
 * at the app root; pairs with `broadcastDataChanged` in the mutation hooks.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BOARD_WINDOW_NAME } from "./boardChannel";

export function useCrossWindowSync() {
  const qc = useQueryClient();
  useEffect(() => {
    let ch: BroadcastChannel;
    try {
      ch = new BroadcastChannel(BOARD_WINDOW_NAME);
    } catch {
      return; // BroadcastChannel unsupported — same-window invalidation still works
    }
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { type?: string; keys?: unknown };
      if (d?.type === "data.invalidate" && Array.isArray(d.keys)) {
        for (const k of d.keys) {
          if (typeof k === "string") qc.invalidateQueries({ queryKey: [k] });
        }
      }
    };
    ch.addEventListener("message", onMsg);
    return () => {
      ch.removeEventListener("message", onMsg);
      ch.close();
    };
  }, [qc]);
}
